import React from 'react';
import dataService from '../services/dataService';
import WikipediaCitation from './WikipediaCitation';

const DataLegend = ({ selectedMetric, stateData, selectedState }) => {
    // Get data source information
    const getDataSourceInfo = () => {
        const source = dataService.dataSources[selectedMetric];
        if (!source) return null;
        
        const sourceNames = {
            'census': 'U.S. Census Bureau',
            'fred': 'Federal Reserve Economic Data (FRED)',
            'static': 'Various Government Sources',
            'dol': 'U.S. Department of Labor'
        };
        
        const lastUpdated = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        
        return {
            name: sourceNames[source.source] || source.source,
            endpoint: source.endpoint,
            lastUpdated: lastUpdated
        };
    };
    if (!selectedMetric || !stateData) {
        return (
            <div style={{ padding: '20px' }}>
                <h3>Legend</h3>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    Select a metric to see the legend
                </p>
            </div>
        );
    }

    const description = dataService.getDataDescription(selectedMetric);
    const sourceInfo = getDataSourceInfo();
    
    // Filter data based on selectedState
    const filteredData = selectedState 
        ? { [selectedState]: stateData[selectedState] }
        : stateData;
    
    // Get values for range calculation
    const values = Object.values(filteredData).filter(v => v !== null && v !== undefined);
    
    if (values.length === 0) {
        return (
            <div style={{ padding: '20px' }}>
                <h3>Legend</h3>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    No data available for {description}
                </p>
            </div>
        );
    }

    const formatValue = (value) => {
        const source = dataService.dataSources[selectedMetric];
        if (!source || !source.unit) return value;

        switch (source.unit) {
            case 'dollars':
                return `$${value.toLocaleString()}`;
            case 'dollars per hour':
                return `$${value.toFixed(2)}`;
            case 'dollars per month':
                return `$${value.toLocaleString()}`;
            case 'percentage':
                return `${value}%`;
            case 'people':
                return value.toLocaleString();
            case 'years':
                return `${value} years`;
            case 'births per 1000 people':
                return `${value}‰`;
            case 'deaths per 1000 births':
                return `${value}‰`;
            case 'incidents per 100,000 people':
                return `${value}/100k`;
            case 'per 100,000 people':
                return `${value}/100k`;
            case 'people per square mile':
                return `${value}/mi²`;
            case 'students per teacher':
                return `${value}:1`;
            case 'degrees Fahrenheit':
                return `${value}°F`;
            case 'Mbps':
                return `${value} Mbps`;
            case 'metric tons per person':
                return `${value}t CO₂`;
            case 'violations per 1000 people':
                return `${value}/1k`;
            case 'percentage of total energy':
                return `${value}%`;
            case 'percentage of bridges':
                return `${value}%`;
            case 'percentage of eligible voters':
                return `${value}%`;
            case 'AQI (lower is better)':
                return `AQI ${value}`;
            case 'index (0-100)':
                return `${value}/100`;
            case 'index (higher is more affordable)':
                return `${value}`;
            case 'score (0-100, higher is better)':
                return `${value}/100`;
            case 'rank (1-50, lower is better)':
                return `#${value}`;
            case 'rank (1-50, lower is better/less corrupt)':
                return `#${value}`;
            case 'grade (A+ to F, A+ is strongest)':
                return value;
            default:
                return value;
        }
    };

    // Handle categorical data (marijuana, deathPenalty, gunLawStrength)
    if (selectedMetric === 'marijuana') {
        const statusCounts = {};
        values.forEach(status => {
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        return (
            <div style={{ padding: '20px' }}>
                <h3>Color Legend</h3>
                <h4 style={{ marginBottom: '15px', color: '#333' }}>{description}</h4>
                
                <div style={{ marginBottom: '20px' }}>
                    {Object.entries({
                        'Legal': '#4CAF50',
                        'Medical only': '#FFC107',
                        'Illegal': '#F44336'
                    }).map(([status, color]) => (
                        <div key={status} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '8px' 
                        }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: color,
                                marginRight: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                            }}></div>
                            <span style={{ fontWeight: '500' }}>
                                {status} ({statusCounts[status] || 0} states)
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (selectedMetric === 'deathPenalty') {
        const statusCounts = {};
        values.forEach(status => {
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        return (
            <div style={{ padding: '20px' }}>
                <h3>Color Legend</h3>
                <h4 style={{ marginBottom: '15px', color: '#333' }}>{description}</h4>
                
                <div style={{ marginBottom: '20px' }}>
                    {Object.entries({
                        'Legal': '#F44336',
                        'Legal (Moratorium)': '#FF9800',
                        'Abolished': '#4CAF50'
                    }).map(([status, color]) => (
                        <div key={status} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '8px' 
                        }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: color,
                                marginRight: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                            }}></div>
                            <span style={{ fontWeight: '500' }}>
                                {status} ({statusCounts[status] || 0} states)
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (selectedMetric === 'gunLawStrength') {
        const statusCounts = {};
        values.forEach(status => {
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        const gradeColors = {
            'A': '#4CAF50', 'A-': '#66BB6A', 'A+': '#2E7D32',
            'B': '#8BC34A', 'B-': '#9CCC65', 'B+': '#689F38',
            'C': '#FFC107', 'C-': '#FFD54F', 'C+': '#FFA000',
            'D': '#FF9800', 'D-': '#FFCC02', 'D+': '#FB8C00',
            'F': '#F44336'
        };

        return (
            <div style={{ padding: '20px' }}>
                <h3>Color Legend</h3>
                <h4 style={{ marginBottom: '15px', color: '#333' }}>{description}</h4>
                
                <div style={{ marginBottom: '20px' }}>
                    {Object.entries(gradeColors).map(([grade, color]) => (
                        statusCounts[grade] > 0 && (
                            <div key={grade} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginBottom: '8px' 
                            }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: color,
                                    marginRight: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px'
                                }}></div>
                                <span style={{ fontWeight: '500' }}>
                                    Grade {grade} ({statusCounts[grade]} states)
                                </span>
                            </div>
                        )
                    ))}
                </div>
            </div>
        );
    }

    // Handle numeric data
    const numericValues = values.filter(v => typeof v === 'number');
    
    // For national statistics, always use all state data
    const allNumericValues = Object.values(stateData).filter(v => typeof v === 'number');
    
    if (numericValues.length === 0) {
        return (
            <div style={{ padding: '20px' }}>
                <h3>Legend</h3>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    No numeric data available for {description}
                </p>
            </div>
        );
    }

    const minValue = Math.min(...allNumericValues);
    const maxValue = Math.max(...allNumericValues);
    const avgValue = allNumericValues.reduce((sum, val) => sum + val, 0) / allNumericValues.length;

    return (
        <div style={{ padding: '20px' }}>
            <h3>{selectedState ? `${dataService.getStateName(selectedState)} Data` : 'Color Legend'}</h3>
            <h4 style={{ marginBottom: '15px', color: '#333' }}>{description}</h4>
            
            <div style={{ marginBottom: '20px' }}>
                {selectedState ? (
                    // Show specific state data
                    <div style={{ 
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        marginBottom: '15px'
                    }}>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2C3E50', marginBottom: '10px' }}>
                            {formatValue(filteredData[selectedState])}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                            {dataService.getStateName(selectedState)}'s {description.toLowerCase()}
                        </div>
                        
                        {/* Show state rank if we have all data */}
                        {Object.keys(stateData).length === 50 && (
                            <div style={{ marginTop: '15px', fontSize: '13px', color: '#666' }}>
                                <strong>State Rank:</strong> #{(() => {
                                    const sorted = Object.entries(stateData)
                                        .filter(([_, v]) => v !== null && v !== undefined && typeof v === 'number')
                                        .sort(([_a, a], [_b, b]) => b - a);
                                    const rank = sorted.findIndex(([state, _]) => state === selectedState) + 1;
                                    return rank > 0 ? rank : 'N/A';
                                })()} out of {Object.keys(stateData).length} states
                            </div>
                        )}
                        
                        {/* National comparison */}
                        {allNumericValues.length > 1 && (
                            <div style={{ marginTop: '10px', fontSize: '13px' }}>
                                <div><strong>National Average:</strong> {formatValue(Math.round(avgValue * 100) / 100)}</div>
                                <div style={{ color: filteredData[selectedState] > avgValue ? '#4CAF50' : '#F44336' }}>
                                    {filteredData[selectedState] > avgValue ? '↑' : '↓'} 
                                    {' '}{Math.abs(((filteredData[selectedState] - avgValue) / avgValue * 100).toFixed(1))}% 
                                    {filteredData[selectedState] > avgValue ? ' above' : ' below'} average
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    // Show legend for all states
                    <>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Range:</strong> {formatValue(minValue)} - {formatValue(maxValue)}
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <strong>Average:</strong> {formatValue(Math.round(avgValue * 100) / 100)}
                        </div>
                    </>
                )}
                
                {!selectedState && (
                    <>
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                <span style={{ minWidth: '60px', fontSize: '12px' }}>Low:</span>
                                <span style={{ marginLeft: '10px', fontWeight: '500' }}>
                                    {formatValue(minValue)}
                                </span>
                            </div>
                            
                            <div style={{
                                height: '20px',
                                background: 'linear-gradient(to right, rgb(255, 0, 100), rgb(0, 255, 100))',
                                border: '1px solid #ddd',
                                borderRadius: '3px',
                                margin: '5px 0'
                            }}></div>
                            
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ minWidth: '60px', fontSize: '12px' }}>High:</span>
                                <span style={{ marginLeft: '10px', fontWeight: '500' }}>
                                    {formatValue(maxValue)}
                                </span>
                            </div>
                        </div>
                        
                        <div style={{
                            fontSize: '12px',
                            color: '#666',
                            fontStyle: 'italic',
                            marginTop: '10px'
                        }}>
                            Red = Lower values, Green = Higher values
                        </div>
                    </>
                )}
                
                {/* Data Source Citation */}
                {sourceInfo && (
                    <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        backgroundColor: '#F0F8FF',
                        borderRadius: '4px',
                        border: '1px solid #B0D4F1',
                        fontSize: '11px'
                    }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#1E90FF' }}>
                            📊 Data Source
                        </div>
                        <div style={{ color: '#333' }}>
                            <strong>Source:</strong> {sourceInfo.name}
                        </div>
                        <div style={{ color: '#666', marginTop: '2px' }}>
                            <strong>Last Updated:</strong> {sourceInfo.lastUpdated}
                        </div>
                    </div>
                )}
                
                {/* Wikipedia Citation */}
                <WikipediaCitation 
                    metricKey={selectedMetric}
                    style={{ marginTop: '12px' }}
                />
            </div>
        </div>
    );
};

export default DataLegend;